import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useUpdateMeeting } from '../../api/useUpdateMeeting';
import { DisplayedMeeting } from '../../types';

const formSchema = z.object({
  description: z.string(),
});

export type MeetingDescriptionFormData = z.infer<typeof formSchema>;

export function useMeetingDescriptionForm(
  meeting: DisplayedMeeting,
  onClose: () => void
) {
  const { isAdmin } = usePermissions();
  const updateMeeting = useUpdateMeeting();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<MeetingDescriptionFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: meeting.description || '',
    },
  });

  const onSubmit = async (data: MeetingDescriptionFormData) => {
    setServerError(null);

    try {
      await updateMeeting.mutateAsync({
        id: meeting.id,
        meeting: { description: data.description },
      });
      onClose();
    } catch {
      setServerError("L'enregistrement a échoué. Vérifiez votre connexion puis réessayez.");
    }
  };

  const date = new Date(meeting.start_time);
  // A midnight start means no time was set: show the day only.
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
  const dateLabel = format(date, hasTime ? "EEEE d MMMM yyyy 'à' HH'h'mm" : 'EEEE d MMMM yyyy', { locale: fr });

  return {
    isAdmin,
    form,
    serverError,
    dateLabel: dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1),
    onSubmit: form.handleSubmit(onSubmit),
    isPending: updateMeeting.isPending,
  };
}
